import { Link } from "react-router-dom";
import { CheckCircle2, DollarSign, Home as HomeIcon, ShieldCheck, Clock, Star, ArrowRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const HERO_BG = "https://images.pexels.com/photos/31602311/pexels-photo-31602311.jpeg";
const TRUST_IMG = "https://images.pexels.com/photos/33175650/pexels-photo-33175650.jpeg";
const TESTIMONIAL_IMG = "https://images.unsplash.com/photo-1514415008039-efa173293080";

const SITUATIONS = [
  { name: "Probate", to: "/sell-probate-house" },
  { name: "Pre-Foreclosure", to: "/stop-foreclosure" },
  { name: "Tax Delinquent", to: "/sell-house-tax-lien" },
  { name: "Inherited", to: "/sell-inherited-house" },
  { name: "Vacant", to: "/sell-house-as-is" },
  { name: "Damaged", to: "/sell-house-as-is" },
  { name: "Behind on Payments", to: "/stop-foreclosure" },
  { name: "Divorce", to: "/sell-house-as-is" },
  { name: "Relocation", to: "/sell-house-as-is" },
];

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 hero-overlay" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32 grid gap-10 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7 text-white">
            <div className="inline-flex items-center gap-2 bg-money/90 text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-5" data-testid="hero-badge">
              <DollarSign size={14} /> Cash offer in 24 hours
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="hero-headline">
              Sell Your House Fast for Cash — <span className="text-money">Any Condition, Any Situation.</span>
            </h1>
            <p className="mt-5 text-lg sm:text-xl text-white/85 max-w-2xl" data-testid="hero-subheadline">
              Aspire Collective Partners LLC buys houses nationwide, direct from owners. No agents, no showings, no repairs. We typically close within 30 days, often sooner — flexible closing timeline depending on your situation.
            </p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-3 max-w-xl">
              {["No fees or commissions", "We pay closing costs", "Flexible closing (~30 days)", "Any condition, as-is"].map((b) => (
                <li key={b} className="flex items-center gap-2 text-white/95">
                  <CheckCircle2 size={20} className="text-money shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div
              className="bg-white/95 backdrop-blur rounded-md border border-white/20 shadow-xl p-7 sm:p-8"
              data-testid="hero-cta-card"
            >
              <p className="text-money font-bold uppercase tracking-widest text-xs mb-2">Free • No Obligation</p>
              <h2 className="font-display text-2xl sm:text-3xl text-navy leading-tight">
                Get a firm cash offer in 24 hours
              </h2>
              <p className="mt-3 text-navy-800/80 text-sm sm:text-base">
                It takes about two minutes. Any condition, any situation — we buy houses nationwide.
              </p>
              <Link
                to="/request-offer?src=home"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-money hover:bg-money/90 text-white text-base sm:text-lg font-bold uppercase tracking-wide px-6 py-4 rounded-md transition"
                data-testid="hero-get-offer-btn"
              >
                Get My Cash Offer <ArrowRight size={20} />
              </Link>
              <p className="mt-4 text-xs text-navy-800/60 text-center">
                No fees. No repairs. You pick the closing date.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST RIBBON */}
      <section className="border-y border-[var(--border-soft)] bg-offwhite" data-testid="trust-ribbon">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-navy">
          {[
            { icon: Clock, label: "24-hr Cash Offer" },
            { icon: HomeIcon, label: "Any Condition" },
            { icon: DollarSign, label: "$0 Fees • $0 Repairs" },
            { icon: ShieldCheck, label: "BBB Accredited Buyer" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center justify-center gap-2 text-sm sm:text-base font-semibold">
              <Icon size={18} className="text-money" /> {label}
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 sm:py-28" data-testid="how-it-works">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-money font-bold uppercase tracking-widest text-sm mb-3">The Process</p>
            <h2 className="font-display text-3xl sm:text-4xl text-navy">Three steps. That's it.</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-12">
            <StepCard n="01" title="Submit your info" body="Tell us about the house — takes 2 minutes. We'll pull neighborhood comps immediately." span="md:col-span-4" />
            <StepCard n="02" title="Get your offer in 24 hours" body="We call with a firm cash offer. No obligation. If you don't like it, walk away — no hard feelings." span="md:col-span-5" />
            <StepCard n="03" title="Close & get paid" body="Pick your closing date. We use a licensed title company. Cash wired to your account." span="md:col-span-3" />
          </div>
        </div>
      </section>

      {/* SITUATIONS BENTO GRID */}
      <section className="py-20 sm:py-24 bg-offwhite" data-testid="situations-grid">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-money font-bold uppercase tracking-widest text-sm mb-3">Who we help</p>
            <h2 className="font-display text-3xl sm:text-4xl text-navy">Real situations. Real solutions.</h2>
            <p className="mt-3 text-lg text-muted">We specialize in the messy, complicated, and time-sensitive.</p>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {SITUATIONS.map((s, i) => (
              <Link
                key={s.name}
                to={s.to}
                className={`group bg-white border border-[var(--border-soft)] rounded-md p-5 hover:border-money hover:shadow-md transition ${i === 0 ? "col-span-2 row-span-2 md:col-span-2 md:row-span-1 lg:col-span-2" : ""}`}
                data-testid={`situation-tile-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="text-xs font-bold uppercase tracking-widest text-money mb-2">Situation</div>
                <div className="font-display text-lg text-navy group-hover:text-money transition-colors">{s.name}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 sm:py-28" data-testid="testimonials">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-5">
            <img src={TESTIMONIAL_IMG} alt="Homeowners at peace after selling" className="rounded-lg w-full h-full object-cover max-h-[420px]" />
          </div>
          <div className="lg:col-span-7 space-y-6">
            <p className="text-money font-bold uppercase tracking-widest text-sm">Sellers speak</p>
            <h2 className="font-display text-3xl sm:text-4xl text-navy">"They handled everything. We just showed up and got paid."</h2>
            <div className="grid gap-6 md:grid-cols-2">
              {[
                { name: "Linda R.", place: "Phoenix, AZ", body: "Mom's probate case dragged on for a year. Aspire Collective Partners LLC closed in 9 days, paid off the attorney fees, and my sister and I finally moved on." },
                { name: "Marcus T.", place: "Detroit, MI", body: "Foreclosure sale was scheduled in 3 weeks. They stopped it. I walked away with cash and a clean credit report." },
              ].map((t) => (
                <div key={t.name} className="bg-white border border-[var(--border-soft)] rounded-md p-5">
                  <div className="flex text-money mb-2">{[...Array(5)].map((_,i)=><Star key={i} size={16} fill="currentColor" />)}</div>
                  <p className="text-navy-800">"{t.body}"</p>
                  <div className="mt-3 text-sm text-muted">— {t.name}, {t.place}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CLOSER TRUST STRIP */}
      <section className="py-20 bg-navy text-white" data-testid="closer-strip">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <h2 className="font-display text-3xl sm:text-4xl">Nationwide cash buyers. Local closings.</h2>
            <p className="mt-4 text-white/80 text-lg">We buy houses in all 50 states. Our closings run through licensed title & escrow attorneys in your local jurisdiction — so you know it's done right.</p>
          </div>
          <div className="lg:col-span-5">
            <img src={TRUST_IMG} alt="Closing handshake" className="rounded-lg w-full h-64 object-cover" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-28" data-testid="faq-section">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <p className="text-money font-bold uppercase tracking-widest text-sm mb-3">Common questions</p>
          <h2 className="font-display text-3xl sm:text-4xl text-navy mb-8">The stuff sellers actually ask us.</h2>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {[
              { q: "How is your offer calculated?", a: "We pull comps in your neighborhood, factor in condition and repair costs, and give you a firm number. No 'inspection surprises' where the offer drops at the last minute." },
              { q: "How fast can you close?", a: "We typically close within 30 days, often sooner. Flexible closing timeline — depending on your situation. If you need time to move, we can also wait up to 6 months. You pick the closing date." },
              { q: "What if my house is a total mess?", a: "Perfect. You literally don't have to clean, repair, or even take out the trash. Take what you want, leave the rest." },
              { q: "Are there any fees?", a: "None. We pay closing costs. What we offer is what you get, wired to your account at closing." },
              { q: "What if I owe more than the house is worth?", a: "Call us anyway. We can sometimes negotiate a short sale with your lender, or explore creative solutions like subject-to, seller financing, or cash offers." },
            ].map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-lg font-semibold text-navy">{f.q}</AccordionTrigger>
                <AccordionContent className="text-base text-navy-800">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pb-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-5xl text-navy">Ready for your offer?</h2>
          <p className="mt-4 text-lg text-muted">Answer a few quick questions. We'll call within 24 hours. No obligation.</p>
          <div className="mt-8">
            <Link
              to="/request-offer?src=home-bottom"
              className="inline-flex items-center justify-center gap-2 bg-money hover:bg-money/90 text-white text-base sm:text-lg font-bold uppercase tracking-wide px-8 py-4 rounded-md transition"
              data-testid="footer-get-offer-btn"
            >
              Get My Cash Offer <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function StepCard({ n, title, body, span }) {
  return (
    <div className={`bg-white border border-[var(--border-soft)] rounded-md p-6 ${span}`}>
      <div className="text-money font-display text-4xl">{n}</div>
      <div className="mt-3 font-display text-xl text-navy">{title}</div>
      <p className="mt-2 text-navy-800/80">{body}</p>
    </div>
  );
}
